import React, { useState, useRef, useEffect } from "react";
import { Input, Progress, Spinner } from "reactstrap";
import { useJobContext } from "../../contexts/job";
import ReactPlayer from "react-player";
import captureVideoFrame from "capture-video-frame";
import Evaporate from "evaporate";
import sparkMD5 from "spark-md5";
import sha256 from "js-sha256";
import { config } from "../../config";
import axios from "axios";
import {
  BlobReader,
  BlobWriter,
  ZipReader,
  ZipWriter,
  Uint8ArrayWriter,
  Uint8ArrayReader,
} from "@zip.js/zip.js";

/* global chrome */

const EXTENSION_ID = "eggbkdjkmoobojkldbkebmlkggafcnom";

const FILE_VALIDATION_TYPES = {
  name: "check_file_name",
  recency: "check_file_recency",
  size: "check_file_size",
};

const FILE_VALIDATION_ERROR_MESSAGES = {
  [FILE_VALIDATION_TYPES.name]: {
    en: "We cannot identify this file based on its name. Please restore the original name of the file or download a new copy and try again.",
    es: "No podemos identificar este archivo según su nombre. Restaure el nombre original del archivo o descargue una nueva copia e inténtelo de nuevo.",
    pt: "Não podemos identificar este arquivo com base em seu nome. Restaure o nome original do arquivo ou baixe uma nova cópia e tente novamente.",
    id: "Kami tidak dapat mengidentifikasi file ini berdasarkan namanya. Harap pulihkan nama asli file tersebut atau unduh salinan baru dan coba lagi.",
    it: "Non possiamo identificare questo file in base al suo nome. Ripristina il nome originale del file o scarica una nuova copia e riprova.",
    fr: "Nous ne pouvons pas identifier ce fichier en fonction de son nom. Veuillez restaurer le nom d'origine du fichier ou télécharger une nouvelle copie et réessayer.",
    de: "Wir können diese Datei nicht anhand ihres Namens identifizieren. Bitte stellen Sie den ursprünglichen Namen der Datei wieder her oder laden Sie eine neue Kopie herunter und versuchen Sie es erneut.",
  },
  [FILE_VALIDATION_TYPES.recency]: {
    en: "This file is older than our requirements. Please download a new copy and try again.",
    es: "Este archivo es más antiguo que nuestros requisitos. Descargue una copia nueva e inténtelo de nuevo.",
    pt: "Este arquivo é mais antigo que nossos requisitos. Baixe uma nova cópia e tente novamente.",
    id: "File ini lebih tua dari persyaratan kami. Silakan unduh salinan baru dan coba lagi.",
    it: "Questo file è più vecchio dei nostri requisiti. Scarica una nuova copia e riprova.",
    fr: "Ce fichier est plus ancien que nos besoins. Veuillez télécharger une nouvelle copie et réessayer.",
    de: "Diese Datei ist älter als unsere Anforderungen. Bitte laden Sie eine neue Kopie herunter und versuchen Sie es erneut.",
  },
  [FILE_VALIDATION_TYPES.size]: {
    en: "This file does not meet our file size requirements.",
    es: "Este archivo no cumple con nuestros requisitos de tamaño de archivo.",
    pt: "Este arquivo não atende aos nossos requisitos de tamanho de arquivo.",
    id: "File ini tidak memenuhi persyaratan ukuran file kami.",
    it: "Questo file non soddisfa i nostri requisiti di dimensione del file.",
    fr: "Ce fichier ne répond pas à nos exigences en matière de taille de fichier.",
    de: "Diese Datei entspricht nicht unseren Anforderungen an die Dateigröße.",
  },
};

function Uploader({
  block,
  showInstructions,
  uploadAttempts,
  setUploadAttempts,
  useMultipartUpload = true,
  fileSource,
}) {
  const effectiveFileSource = fileSource || "default";

  const {
    baseJobURL,
    platform,
    lang,
    values,
    setValues,
    goNext,
    handleTerminate,
    handleUserTerminate,
    embedded,
    embeddingHost,
    cid,
    srid,
  } = useJobContext();

  console.log(`Uploader: cid=${cid} srid=${srid}`);

  const maxValidationAttempts = 3;
  const captureType = block.screen_capture.data_format;

  const getString = (name, lang) => {
    return {
      status_text_1: {
        en: (
          <p>
            Use the link above to upload your {captureType}, or if you haven't
            completed the steps yet, tap the link below to return to the
            instructions.
          </p>
        ),
        pt: (
          <p>
            Use o link acima para enviar seu {captureType} ou, se ainda não
            tiver concluído as etapas, toque no link abaixo para retornar às
            instruções.
          </p>
        ),
        es: (
          <p>
            Use el enlace anterior para cargar su {captureType} o, si aún no ha
            completado los pasos, toque el enlace a continuación para volver a
            las instrucciones.
          </p>
        ),
        id: (
          <p>
            Gunakan tautan di atas untuk mengunggah file Anda, atau jika Anda
            belum menyelesaikan langkah-langkahnya, ketuk tautan di bawah untuk
            kembali ke petunjuk.
          </p>
        ),
        it: (
          <p>
            Utilizza il link sopra per caricare il tuo {captureType} o, se non
            hai completato i passaggi, tuttavia, tocca il link sottostante per
            tornare alle istruzioni.
          </p>
        ),
        fr: (
          <p>
            Utilisez le lien ci-dessus pour télécharger votre {captureType}, ou
            si vous n'avez pas terminé les étapes Pourtant, appuyez sur le lien
            ci-dessous pour revenir aux instructions.
          </p>
        ),
        de: (
          <p>
            Verwenden Sie den Link oben, um Ihren {captureType} hochzuladen,
            oder falls Sie die Schritte noch nicht abgeschlossen haben Tippen
            Sie dennoch auf den Link unten, um zu den Anweisungen
            zurückzukehren.
          </p>
        ),
      },
      status_text_1_multi_upload: {
        en: (
          <p>
            Use the link above to upload your {captureType}s (you can select
            multiple files), or if you haven't completed the steps yet, tap the
            link below to return to the instructions.
          </p>
        ),
        pt: (
          <p>
            Use o link acima para enviar seus {captureType}s (você pode
            selecionar vários arquivos) ou, se ainda não tiver concluído as
            etapas, toque no link abaixo para retornar às instruções.
          </p>
        ),
        es: (
          <p>
            Use el enlace anterior para cargar sus {captureType}s (puede
            seleccionar múltiples archivos) o, si aún no ha completado los
            pasos, toque el enlace a continuación para volver a las
            instrucciones.
          </p>
        ),
        id: (
          <p>
            Gunakan tautan di atas untuk mengunggah {captureType} Anda (Anda
            dapat memilih beberapa file), atau jika Anda belum menyelesaikan
            langkah-langkahnya, ketuk tautan di bawah untuk kembali ke petunjuk.
          </p>
        ),
        it: (
          <p>
            Utilizza il link sopra per caricare i tuoi {captureType} (puoi
            selezionare più file) o, se non hai completato i passaggi, tocca il
            link sottostante per tornare alle istruzioni.
          </p>
        ),
        fr: (
          <p>
            Utilisez le lien ci-dessus pour télécharger vos {captureType}s (vous
            pouvez sélectionner plusieurs fichiers), ou si vous n'avez pas
            terminé les étapes, appuyez sur le lien ci-dessous pour revenir aux
            instructions.
          </p>
        ),
        de: (
          <p>
            Verwenden Sie den Link oben, um Ihre {captureType}s hochzuladen (Sie
            können mehrere Dateien auswählen), oder falls Sie die Schritte noch
            nicht abgeschlossen haben, tippen Sie auf den Link unten, um zu den
            Anweisungen zurückzukehren.
          </p>
        ),
      },
      status_text_fetch_data_1: {
        en: (
          <p>
            Orders are auto-retrieved and uploaded. If an error occurs, tap the
            link for instructions.
          </p>
        ),
        pt: (
          <p>
            Os pedidos são recuperados e enviados automaticamente. Se houver
            erro, toque no link para as instruções.
          </p>
        ),
        es: (
          <p>
            Los pedidos se recuperan y suben automáticamente. Si ocurre un
            error, toque el enlace para las instrucciones.
          </p>
        ),
        id: (
          <p>
            Pesanan diambil dan diunggah secara otomatis. Jika terjadi
            kesalahan, ketuk tautan untuk petunjuk.
          </p>
        ),
        it: (
          <p>
            Gli ordini vengono recuperati e caricati automaticamente. In caso di
            errore, tocca il link per le istruzioni.
          </p>
        ),
        fr: (
          <p>
            Les commandes sont récupérées et téléversées automatiquement. En cas
            d'erreur, appuyez sur le lien pour les instructions.
          </p>
        ),
        de: (
          <p>
            Bestellungen werden automatisch abgerufen und hochgeladen. Bei
            Fehlern tippen Sie auf den Link für die Anweisungen.
          </p>
        ),
      },
      upload_link_text_1: {
        en: `Tap to select your ${captureType}`,
        pt: `Clique para selecionar seu ${captureType}`,
        es: `Toque para seleccionar su ${captureType}`,
        id: `Ketuk untuk memilih ${captureType} Anda`,
        it: `Tocca per selezionare il tuo ${captureType}`,
        fr: `Appuyez pour sélectionner votre ${captureType}`,
        de: `Tippen Sie hier, um Ihren ${captureType} auszuwählen.`,
      },
      multi_upload_link_text_1: {
        en: `Tap to select multiple ${captureType}s`,
        pt: `Clique para selecionar múltiplos ${captureType}s`,
        es: `Toque para seleccionar múltiples ${captureType}s`,
        id: `Ketuk untuk memilih beberapa ${captureType}`,
        it: `Tocca per selezionare più ${captureType}`,
        fr: `Appuyez pour sélectionner plusieurs ${captureType}s`,
        de: `Tippen Sie hier, um mehrere ${captureType}s auszuwählen.`,
      },
      status_text_2: {
        en: <p>Tap the link above to try uploading another {captureType}.</p>,
        pt: (
          <p>Clique no link acima para tentar carregar outro {captureType}.</p>
        ),
        es: <p>Toque el enlace de arriba para intentar cargar otro archivo.</p>,
        it: (
          <p>
            Tocca il link qui sopra per provare a caricare un altro{" "}
            {captureType}.
          </p>
        ),
        fr: (
          <p>
            Appuyez sur le lien ci-dessus pour essayer de télécharger un autre{" "}
            {captureType}.
          </p>
        ),
        de: (
          <p>
            Tippen Sie auf den Link oben, um zu versuchen, einen weiteren{" "}
            {captureType} hochzuladen.
          </p>
        ),
      },
      status_text_2_multi_upload: {
        en: (
          <p>
            Tap the link above to try uploading your {captureType}s again (you
            can select multiple files).
          </p>
        ),
        pt: (
          <p>
            Clique no link acima para tentar carregar seus {captureType}s
            novamente (você pode selecionar vários arquivos).
          </p>
        ),
        es: (
          <p>
            Toque el enlace de arriba para intentar cargar sus {captureType}s de
            nuevo (puede seleccionar múltiples archivos).
          </p>
        ),
        it: (
          <p>
            Tocca il link qui sopra per provare a caricare di nuovo i tuoi{" "}
            {captureType} (puoi selezionare più file).
          </p>
        ),
        fr: (
          <p>
            Appuyez sur le lien ci-dessus pour essayer de télécharger à nouveau
            vos {captureType}s (vous pouvez sélectionner plusieurs fichiers).
          </p>
        ),
        de: (
          <p>
            Tippen Sie auf den Link oben, um zu versuchen, Ihre {captureType}s
            erneut hochzuladen (Sie können mehrere Dateien auswählen).
          </p>
        ),
      },
      upload_link_text_2: {
        en: `Select another ${captureType}`,
        pt: `Selecione outro ${captureType}`,
        es: `Seleccione otro ${captureType}`,
        id: `Pilih ${captureType} lain`,
        it: `Seleziona un altro ${captureType}`,
        fr: `Sélectionnez un autre ${captureType}`,
        de: `Wählen Sie einen anderen ${captureType}`,
      },
      multi_upload_link_text_2: {
        en: `Select your ${captureType}s again`,
        pt: `Selecione seus ${captureType}s novamente`,
        es: `Seleccione sus ${captureType}s de nuevo`,
        id: `Pilih ${captureType} Anda lagi`,
        it: `Seleziona di nuovo i tuoi ${captureType}`,
        fr: `Sélectionnez à nouveau vos ${captureType}s`,
        de: `Wählen Sie Ihre ${captureType}s erneut`,
      },
      status_text_with_error_2: {
        en: (
          <p>
            <span className="error">Validation failed</span> — we couldn't find
            the required content in the {captureType}. Go back to the
            instructions page and try again.
          </p>
        ),
        pt: (
          <p>
            <span className="error">Falha na validação</span> — não foi possível
            encontrar o conteúdo necessário no {captureType}. Volte para a
            página de instruções e tente novamente.
          </p>
        ),
        es: (
          <p>
            <span className="error">La validación falló</span> — no pudimos
            encontrar el contenido requerido en el archivo. Vuelve a la página
            de instrucciones e inténtalo de nuevo.
          </p>
        ),
        id: (
          <p>
            <span className="error">Validasi gagal</span> — kami tidak dapat
            menemukan konten yang diperlukan dalam file. Kembali ke halaman
            petunjuk dan coba lagi.
          </p>
        ),
        it: (
          <p>
            <span className="error">Convalida non riuscita</span>: non siamo
            riusciti a trovare il file contenuto richiesto in {captureType}.
            Torna alla pagina delle istruzioni e riprova.
          </p>
        ),
        fr: (
          <p>
            <span className="error">Échec de la validation</span> — nous n'avons
            pas trouvé le contenu requis dans le {captureType}. Revenez à la
            page d'instructions et réessayez.
          </p>
        ),
        de: (
          <p>
            <span className="error">Validierung fehlgeschlagen</span> - wir
            konnten das nicht finden Erforderlicher Inhalt im {captureType}.
            Gehen Sie zurück zur Seite mit den Anweisungen und versuchen Sie es
            erneut.
          </p>
        ),
      },
      status_text_with_error_3: {
        en: (
          <p>
            <span className="error">Validation failed again</span> — we couldn't
            find the required content in the {captureType}. Tap above to select
            another {captureType} and try again.
          </p>
        ),
        pt: (
          <p>
            <span className="error">Falha na validação novamente</span> — não
            foi possível encontrar o conteúdo necessário no {captureType}. Volte
            para a página de instruções e tente novamente.
          </p>
        ),
        es: (
          <p>
            <span className="error">La validación falló nuevamente</span> – no
            pudimos encontrar el contenido requerido en el archivo. Vuelve a la
            página de instrucciones e inténtalo de nuevo.
          </p>
        ),
        id: (
          <p>
            <span className="error">Validasi gagal lagi</span> – kami tidak
            dapat menemukan konten yang diperlukan dalam file. Kembali ke
            halaman petunjuk dan coba lagi.
          </p>
        ),
        it: (
          <p>
            <span className="error">Convalida nuovamente fallita</span>: non
            siamo riusciti a trovare i dati richiesti contenuto in {captureType}
            . Tocca sopra per selezionare un altro {captureType} e riprova.
          </p>
        ),
        fr: (
          <p>
            <span className="error">La validation a de nouveau échoué</span> :
            nous n'avons pas trouvé les éléments requis contenu dans le{" "}
            {captureType}. Appuyez ci-dessus pour sélectionner un autre{" "}
            {captureType} et réessayez.
          </p>
        ),
        de: (
          <p>
            <span className="error">Validierung erneut fehlgeschlagen</span> –
            wir konnten das Erforderliche nicht finden Inhalt im {captureType}.
            Tippen Sie oben, um einen anderen {captureType} auszuwählen und
            versuchen Sie es erneut.
          </p>
        ),
      },
      status_text_with_error_4: {
        en: (
          <p>
            <span className="error">Validation failed</span> — sorry, we still
            couldn't find the required content. Tap the link below to continue.
          </p>
        ),
        pt: (
          <p>
            <span className="error">Falha na validação</span> — desculpe, ainda
            não foi possível encontrar o conteúdo necessário. Toque no link
            abaixo para continuar.
          </p>
        ),
        es: (
          <p>
            <span className="error">La validación falló</span> — lo sentimos,
            todavía no pudimos encontrar el contenido requerido. Toque el enlace
            de abajo para continuar.
          </p>
        ),
        id: (
          <p>
            <span className="error">Validasi gagal lagi</span> — maaf, kami
            masih tidak dapat menemukan konten yang diperlukan. Ketuk tautan di
            bawah untuk melanjutkan.
          </p>
        ),
        it: (
          <p>
            <span className="error">Convalida non riuscita</span>: spiacenti,
            non siamo ancora riusciti a trovare il file contenuto richiesto.
            Tocca il link sottostante per continuare.
          </p>
        ),
        fr: (
          <p>
            <span className="error">Échec de la validation</span> — désolé, nous
            n'avons toujours pas trouvé le contenu requis. Appuyez sur le lien
            ci-dessous pour continuer.
          </p>
        ),
        de: (
          <p>
            <span className="error">Validierung fehlgeschlagen</span> – leider
            konnten wir das immer noch nicht finden erforderliche Inhalte.
            Tippen Sie auf den Link unten, um fortzufahren.
          </p>
        ),
      },

      validation_1_validating: {
        en: <p>Validating {captureType}...</p>,
        pt: <p>Validando vídeo...</p>,
        es: <p>Validando video...</p>,
        id: <p>Memvalidasi video...</p>,
        it: <p>Convalida {captureType} in corso...</p>,
        fr: <p>Validation de {captureType}...</p>,
        de: <p>Validierung von {captureType}...</p>,
      },
      validation_2_preparing: {
        en: <p>Validated, preparing upload...</p>,
        pt: <p>Validado, preparando upload...</p>,
        es: <p>Validado, preparando carga...</p>,
        id: <p>Divalidasi, menyiapkan unggahan...</p>,
        it: <p>Convalidato, preparazione caricamento...</p>,
        fr: <p>Validé, préparation du téléchargement...</p>,
        de: <p>Validiert, Upload wird vorbereitet...</p>,
      },
      validation_2b_removing_media: {
        en: <p>Removing media from zip file...</p>,
        pt: <p>Removendo mídia do arquivo zip...</p>,
        es: <p>Eliminando medios del archivo zip...</p>,
        id: <p>Menghapus media dari file zip...</p>,
        it: <p>Rimozione dei media dal file zip...</p>,
        fr: <p>Suppression des médias du fichier zip...</p>,
        de: <p>Entfernen von Mediendateien aus der ZIP-Datei...</p>,
      },
      validation_2b_processing_files: {
        en: <p>Processing files...</p>,
        pt: <p>Processando arquivos...</p>,
        es: <p>Procesando archivos...</p>,
        id: <p>Memproses file...</p>,
        it: <p>Elaborazione dei file in corso...</p>,
        fr: <p>Traitement des fichiers...</p>,
        de: <p>Dateien werden verarbeitet...</p>,
      },
      validation_3_uploading: {
        en: <p>Uploading...</p>,
        pt: <p>Carregando...</p>,
        es: <p>Cargando...</p>,
        id: <p>Mengunggah...</p>,
        it: <p>Caricamento in corso...</p>,
        fr: <p>Téléchargement...</p>,
        de: <p>Hochladen...</p>,
      },
      validation_4_finished: {
        en: <p>Finished upload</p>,
        pt: <p>Concluído</p>,
        es: <p>Finalizada</p>,
        id: <p>Selesai mengunggah</p>,
        it: <p>Caricamento terminato</p>,
        fr: <p>Téléchargement terminé</p>,
        de: <p>Hochladen abgeschlossen</p>,
      },
      validation_5_error_uploading: {
        en: <p className="error">Error uploading</p>,
        pt: <p className="error">Erro ao enviar</p>,
        es: <p className="error">Error al cargar</p>,
        id: <p className="error">Kesalahan mengunggah</p>,
        it: <p className="error">Errore durante il caricamento</p>,
        fr: <p className="error">Erreur de téléchargement</p>,
        de: <p className="error">Fehler beim Hochladen</p>,
      },
      validation_6_error_preparing: {
        en: <p className="error">Error preparing upload</p>,
        pt: <p className="error">Erro ao preparar upload</p>,
        es: <p className="error">Error al preparar cargar</p>,
        id: <p className="error">Kesalahan mempersiapkan unggah</p>,
        it: (
          <p className="error">
            Errore durante la preparazione del caricamento
          </p>
        ),
        fr: (
          <p className="error">
            Erreur lors de la préparation du téléchargement
          </p>
        ),
        de: <p className="error">Fehler beim Vorbereiten des Uploads</p>,
      },
      validation_7_trying_again: {
        en: <p>Validation failed, trying again...</p>,
        pt: <p>Falha na validação, tentando novamente</p>,
        es: <p>Validación fallida, intentándolo de nuevo</p>,
        id: <p>Validasi gagal, mencoba lagi</p>,
        it: <p>Convalida fallita, riprovare...</p>,
        fr: <p>Échec de la validation, réessayez...</p>,
        de: <p>Validierung fehlgeschlagen, es wird erneut versucht...</p>,
      },
      validation_8_error_during_validation: {
        en: <p className="error">Error during validation</p>,
        pt: <p className="error">Erro durante a validação</p>,
        es: <p className="error">Error durante la validación</p>,
        id: <p className="error">Kesalahan selama validasi</p>,
        it: <p className="error">Errore durante la convalida</p>,
        fr: <p className="error">Erreur lors de la validation</p>,
        de: <p className="error">Fehler während der Validierung</p>,
      },

      sampling_content: {
        en: <p>Sampling content for validation...</p>,
        pt: <p>Processando conteúdo de vídeo para validação</p>,
        es: <p>Muestra de contenido de video para validación</p>,
        id: <p>Konten video Sampling untuk validasi</p>,
        it: <p>Campionamento dei contenuti per la convalida...</p>,
        fr: <p>Échantillonnage de contenu pour validation...</p>,
        de: <p>Probenahme von Inhalten zur Validierung...</p>,
      },

      wrong_type_video: {
        en: (
          <p>
            <span className="error">That file was the wrong type</span> — the
            upload must be a video file captured on your device. Please tap the
            link above to select a video.
          </p>
        ),
        pt: (
          <p>
            <span className="error">Esse arquivo era do tipo errado</span> — o
            upload deve ser um arquivo de vídeo capturado em seu dispositivo.
            Toque no link acima para selecionar um vídeo.
          </p>
        ),
        es: (
          <p>
            <span className="error">Ese archivo era del tipo incorrecto</span> —
            la carga debe ser un archivo de video capturado en su dispositivo.
            Toca el enlace de arriba para seleccionar un video.
          </p>
        ),
        id: (
          <p>
            <span className="error">Jenis file tersebut salah</span> — unggahan
            harus berupa file video yang diambil di perangkat Anda. Ketuk tautan
            di atas untuk memilih video.
          </p>
        ),
        it: (
          <p>
            <span className="error">Il file era del tipo sbagliato</span>: il
            caricamento deve essere un video file catturato sul tuo dispositivo.
            Tocca il link qui sopra per selezionare un video.
          </p>
        ),
        fr: (
          <p>
            <span className="error">Ce fichier n'était pas du bon type</span> :
            la mise en ligne doit être une vidéo fichier capturé sur votre
            appareil. Veuillez appuyer sur le lien ci-dessus pour sélectionner
            une vidéo.
          </p>
        ),
        de: (
          <p>
            <span className="error">Diese Datei hatte den falschen Typ</span> –
            der Upload muss ein Video sein Datei, die auf Ihrem Gerät erfasst
            wurde. Bitte tippen Sie auf den Link oben, um ein Video auszuwählen.
          </p>
        ),
      },
      wrong_type_image: {
        en: (
          <p>
            <span className="error">That file was the wrong type</span> — the
            upload must be an image file captured on your device. Please tap the
            link above to select an image.
          </p>
        ),
        pt: (
          <p>
            <span className="error">Esse arquivo era do tipo errado</span> — o
            upload deve ser um arquivo de imagem capturado em seu dispositivo.
            Toque no link acima para selecionar uma imagem.
          </p>
        ),
        es: (
          <p>
            <span className="error">Ese archivo era del tipo incorrecto</span> —
            la carga debe ser un archivo de imagen capturado en su dispositivo.
            Toque el enlace de arriba para seleccionar una imagen.
          </p>
        ),
        id: (
          <p>
            <span className="error">Jenis file tersebut salah</span> — file yang
            diunggah harus berupa file gambar yang diambil di perangkat Anda.
            Ketuk tautan di atas untuk memilih gambar.
          </p>
        ),
        it: (
          <p>
            <span className="error">Il file era del tipo sbagliato</span>: il
            caricamento deve essere un'immagine file catturato sul tuo
            dispositivo. Tocca il link qui sopra per selezionare un'immagine.
          </p>
        ),
        fr: (
          <p>
            <span className="error">Ce fichier n'était pas du bon type</span> :
            le téléchargement doit être une image fichier capturé sur votre
            appareil. Veuillez appuyer sur le lien ci-dessus pour sélectionner
            une image.
          </p>
        ),
        de: (
          <p>
            <span className="error">Diese Datei hatte den falschen Typ</span> –
            der Upload muss ein Bild sein Datei, die auf Ihrem Gerät erfasst
            wurde. Bitte tippen Sie auf den Link oben, um ein Bild auszuwählen.
          </p>
        ),
      },

      upload_successful: {
        en: "Upload successful",
        pt: "Envio bem-sucedido",
        es: "Carga exitosa",
        id: "Pengunggahan berhasil",
        it: "Caricamento riuscito",
        fr: "Téléchargement réussi",
        de: "Hochladen erfolgreich",
      },

      back_to_instructions: {
        en: "Go back to instructions",
        pt: "Voltar para as instruções",
        es: "Volver a las instrucciones",
        id: "Kembali ke petunjuk",
        it: "Torna alle istruzioni",
        fr: "Revenir aux instructions",
        de: "Zurück zu den Anweisungen",
      },
      skip_this: {
        en: "I want to skip this",
        pt: "Quero pular esta",
        es: "Quiero omitir este",
        id: "Saya ingin melewatkan",
        it: "Voglio saltare questo",
        fr: "Je veux sauter ça",
        de: "Ich möchte das überspringen",
      },
    }[name][lang];
  };

  const uploadAttemptConfig = [
    {
      uploadLinkText: getString(
        block.screen_capture.allow_multiple_file_upload
          ? "multi_upload_link_text_1"
          : "upload_link_text_1",
        lang
      ),
      statusText: getString(
        block.screen_capture.allow_multiple_file_upload
          ? "status_text_1_multi_upload"
          : "status_text_1",
        lang
      ),
      allowUpload: false,
      showInstructionsLink: true,
      showTerminateLink: false,
    },
    {
      uploadLinkText: getString(
        block.screen_capture.allow_multiple_file_upload
          ? "multi_upload_link_text_2"
          : "upload_link_text_2",
        lang
      ),
      statusTextWithError: getString("status_text_with_error_2", lang),
      statusText: getString(
        block.screen_capture.allow_multiple_file_upload
          ? "status_text_2_multi_upload"
          : "status_text_2",
        lang
      ),
      allowUpload: false,
      showInstructionsLink: true,
      showTerminateLink: false,
    },
    {
      uploadLinkText: getString(
        block.screen_capture.allow_multiple_file_upload
          ? "multi_upload_link_text_2"
          : "upload_link_text_2",
        lang
      ),
      statusTextWithError: getString("status_text_with_error_3", lang),
      statusText: getString(
        block.screen_capture.allow_multiple_file_upload
          ? "status_text_2_multi_upload"
          : "status_text_2",
        lang
      ),
      allowUpload: true,
      showInstructionsLink: true,
      showTerminateLink: false,
    },
    {
      uploadLinkText: "",
      statusTextWithError: getString("status_text_with_error_4", lang),
      allowUpload: false,
      showInstructionsLink: false,
      showTerminateLink: false,
      autoAdvanceWithTerminate: true,
    },
  ];

  function fetchOrderData() {
    return new Promise((resolve, reject) => {
      try {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: "fetchGeneratedData" },
          function (response) {
            if (chrome.runtime.lastError) {
              reject(chrome.runtime.lastError);
            } else if (response && response.success) {
              resolve(response.data);
            } else {
              reject(
                new Error(response?.error || "Unknown error fetching data")
              );
            }
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  function createCSV(flattenedItems) {
    const header = [
      "Account Name",
      "Product Name",
      "Price",
      "Quantity",
      "Order Date",
      "Order Number",
      "Order Type",
      "Order Status",
    ];
    const rows = flattenedItems.map((item) => [
      `"${item.accountName.replace(/"/g, '""')}"`,
      `"${item.productName.replace(/"/g, '""')}"`,
      `"${item.price.replace(/"/g, '""')}"`,
      `"${item.quantity.replace(/"/g, '""')}"`,
      `"${item.orderDate.replace(/"/g, '""')}"`,
      `"${item.orderNumber.replace(/"/g, '""')}"`,
      `"${item.orderType.replace(/"/g, '""')}"`,
      `"${item.orderStatus.replace(/"/g, '""')}"`,
    ]);
    return [header.join(","), ...rows.map((row) => row.join(","))].join("\n");
  }

  function getFlattenedItems(orders) {
    return orders.reduce((acc, order) => {
      const items = (order.items || []).map((item) => ({
        accountName: order.accountName || "",
        orderNumber: order.orderNumber || "",
        orderDate: order.orderDate || "",
        productName: item.productName || "",
        quantity: item.quantity
          ? item.quantity.replace(/qty|quantity/gi, "").trim()
          : "",
        price: item.price || "",
        orderType: order.orderType || "",
        orderStatus: item.orderStatus || "Shopped",
      }));
      return acc.concat(items);
    }, []);
  }

  function fetchYoutubeActivity() {
    return new Promise((resolve, reject) => {
      try {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: "fetchGoogleActivityData" },
          function (response) {
            if (chrome.runtime.lastError) {
              reject(chrome.runtime.lastError);
            } else if (response && response.success) {
              resolve(response.data);
            } else {
              reject(
                new Error(response?.error || "Unknown error fetching data")
              );
            }
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  function createYoutubeCSV(data = []) {
    if (!data.length) return "";

    const fields = [
      "date",
      "time",
      "title",
      "event",
      "channel",
      "url",
      "device",
    ];
    // header row
    let csv = fields.join(",") + "\n";

    data.forEach((item) => {
      const row = fields.map((f) => {
        let v = item[f] || "";
        // escape quotes/newlines/commas
        if (/[,"\n]/.test(v)) {
          v = `"${v.replace(/"/g, '""')}"`;
        }
        return v;
      });
      csv += row.join(",") + "\n";
    });

    return csv;
  }

  function fetchRobloxData() {
    return new Promise((resolve, reject) => {
      try {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: "fetchRobloxData" },
          function (response) {
            if (chrome.runtime.lastError) {
              reject(chrome.runtime.lastError);
            } else if (response && response.success) {
              resolve(response.data);
            } else {
              reject(
                new Error(response?.error || "Unknown error fetching data")
              );
            }
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  function fetchPerplexityData() {
    return new Promise((resolve, reject) => {
      try {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: "fetchPerplexityData" },
          function (response) {
            if (chrome.runtime.lastError) {
              reject(chrome.runtime.lastError);
            } else if (response && response.success) {
              resolve(response.data);
            } else {
              reject(
                new Error(response?.error || "Unknown error fetching data")
              );
            }
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  function fetchChatGPTData() {
    return new Promise((resolve, reject) => {
      try {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: "fetchChatgptData" },
          function (response) {
            if (chrome.runtime.lastError) {
              reject(chrome.runtime.lastError);
            } else if (response && response.success) {
              resolve(response.data);
            } else {
              reject(
                new Error(response?.error || "Unknown error fetching data")
              );
            }
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  }


  const [fileInputValue, setFileInputValue] = useState("");
  const [videoFilePath, setVideoFilePath] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [extractingFrames, setExtractingFrames] = useState(false);
  const [uploadingWithoutValidation, setUploadingWithoutValidation] =
    useState(false);
  const [frameTimesToCapture, setFrameTimesToCapture] = useState([]);
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [currentFrameTime, setCurrentFrameTime] = useState(null);
  const [isFinished, setIsFinished] = useState(false);
  const [firstTimeLoaded, setFirstTimeLoaded] = useState(true);
  const [newVideoToValidate, setNewVideoToValidate] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [validationAttempts, setValidationAttempts] = useState(1);
  const [showInstructionsButton, setShowInstructionsButton] = useState(!!showInstructions);
  const [showTerminateButton, setShowTerminateButton] = useState(false);
  const [uploadCompletionStatus, setUploadCompletionStatus] = useState(null);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [statusMessage, setStatusMessage] = useState(
    effectiveFileSource === "localStorage"
      ? getString("status_text_fetch_data_1", lang)
      : uploadAttemptConfig[uploadAttempts].statusText
  );
  const [uploadProgressPercentage, setUploadProgressPercentage] = useState(0);

  const [mediaUploadId, setMediaUploadId] = useState(null);

  //New State files to show and manage multiple selected files before upload
  const [selectedMultiFiles, setSelectedMultiFiles] = useState(null);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);

  const [zipProgress, setZipProgress] = useState(0);
  console.log("Progress", zipProgress);

  const video = useRef(null);

  const evaporateUploader = Evaporate.create({
    signerUrl: `${baseJobURL}/generate-multipart-upload-signature`,
    aws_key: config.WORKSHOP_AWS_KEY,
    bucket: config.USER_UPLOAD_S3_BUCKET,
    cloudfront: true,
    s3Acceleration: true,
    computeContentMd5: true,
    cryptoMd5Method: (d) => btoa(sparkMD5.ArrayBuffer.hash(d, true)),
    cryptoHexEncodedHash256: sha256,
    logging: true,
  });

  // special code for various embedding hosts
  if (embedded && firstTimeLoaded) {
    if (embeddingHost === "alchemer") {
      // we do some custom scrolling, because Alchemer has weird spacing and overflow content
      window.parent.postMessage(
        {
          messageType: "measure-embedded-job-message",
          action: "scroll-to-top",
        },
        "*"
      );
      setFirstTimeLoaded(false);
    }
  }

  useEffect(() => {
    // Only run if block is loaded and the data type matches.
    if (
      effectiveFileSource === "localStorage" &&
      block?.screen_capture.data_type === "walmart_order_history"
    ) {
      (async () => {
        try {
          // Optionally, you can log or use the timestamp as needed.
          const orders = await fetchOrderData();
          const flattenedItems = getFlattenedItems(orders);
          const csvContent = createCSV(flattenedItems);
          const blob = new Blob([csvContent], { type: "text/csv" });
          const file = new File([blob], "walmart-orders.csv", {
            type: blob.type,
          });
          setVideoFile(file);
          setUploadingWithoutValidation(true);
          setVideoFilePath(URL.createObjectURL(file));
        } catch (error) {
          console.error("Error fetching or processing order data:", error);
        }
      })();
    } else if (
      effectiveFileSource === "localStorage" &&
      block?.screen_capture.data_type === "youtube_data_upload"
    ) {
      (async () => {
        try {
          const youtubeData = await fetchYoutubeActivity();

          const csvContent = createYoutubeCSV(youtubeData);

          const blob = new Blob([csvContent], { type: "text/csv" });
          const file = new File([blob], "youtube-activity.csv", {
            type: blob.type,
          });
          setVideoFile(file);
          setUploadingWithoutValidation(true);
          setVideoFilePath(URL.createObjectURL(file));
        } catch (err) {
          console.error("Error fetching or processing YouTube data:", err);
        }
      })();
    } else if (
      effectiveFileSource === "localStorage" &&
      block?.screen_capture.data_type === "roblox_transaction_history"
    ) {
      (async () => {
        try {
          const robloxResponse = await fetchRobloxData();
          const robloxData = robloxResponse?.data || robloxResponse;
          const jsonContent = JSON.stringify(robloxData);
          const blob = new Blob([jsonContent], { type: "application/json" });
          const file = new File([blob], "roblox-transactions.json", {
            type: blob.type,
          });
          setVideoFile(file);
          setUploadingWithoutValidation(true);
          setVideoFilePath(URL.createObjectURL(file));
        } catch (err) {
          console.error("Error fetching or processing Roblox data:", err);
        }
      })();
    } else if (
      effectiveFileSource === "localStorage" &&
      block?.screen_capture.data_type === "perplexity_ai_history"
    ) {
      (async () => {
        try {
          const perplexityResponse = await fetchPerplexityData();
          const perplexityData = perplexityResponse?.data || perplexityResponse;
          const jsonContent = JSON.stringify(perplexityData);
          const blob = new Blob([jsonContent], { type: "application/json" });
          const file = new File([blob], "perplexity-history.json", {
            type: blob.type,
          });
          setVideoFile(file);
          setUploadingWithoutValidation(true);
          setVideoFilePath(URL.createObjectURL(file));
        } catch (err) {
          console.error("Error fetching or processing Perplexity data:", err);
        }
      })();
    } else if (
      effectiveFileSource === "localStorage" &&
      block?.screen_capture.data_type === "chatgpt_ad_history"
    ) {
      (async () => {
        try {
          const chatgptResponse = await fetchChatGPTData();
          const chatgptData = chatgptResponse?.data || chatgptResponse;
          const jsonContent = JSON.stringify(chatgptData);
          const blob = new Blob([jsonContent], { type: "application/json" });
          const file = new File([blob], "chatgpt-ad-history.json", {
            type: blob.type,
          });
          setVideoFile(file);
          setUploadingWithoutValidation(true);
          setVideoFilePath(URL.createObjectURL(file));
        } catch (err) {
          console.error("Error fetching or processing ChatGPT data:", err);
        }
      })();
    }
  }, [block, effectiveFileSource]);

  useEffect(() => {
    if (extractingFrames) {
      if (captureType === "video") {
        if (currentFrameTime === null) {
          if (frameTimesToCapture.length > 0) {
            const nextFrameTime = frameTimesToCapture[0];
            setCurrentFrameTime(nextFrameTime);
            setFrameTimesToCapture(frameTimesToCapture.slice(1));
            video.current.seekTo(nextFrameTime);
            console.log(`Seeking to: ${nextFrameTime}`);
          } else {
            const framesToValidate = [...capturedFrames];
            setExtractingFrames(false);
            setCapturedFrames([]);
            runValidationWithCapturedFrames(framesToValidate);
          }
        }
      } else if (captureType === "image") {
        setExtractingFrames(false);
        setCapturedFrames([]);
        const framesToValidate = [{ blob: videoFile, format: "jpeg" }];
        runValidationWithCapturedFrames(framesToValidate);
      }
    }
    if (uploadingWithoutValidation) {
      setUploadingWithoutValidation(false);
      uploadFile();
    }
  });

  //Use Effect to update progress for zip
  // useEffect(() => {
  //   if (isProcessingFiles && zipProgress < 100) {
  //     // setStatusMessage(`Merging zip files ${zipProgress}%`);
  //     // setZipProgress((progress * 100).toFixed(1));
  //     setStatusMessage('Processing ')
  //   }
  // }, [zipProgress, isProcessingFiles]);

  const handleSeekFinished = () => {
    console.log(`Extract frame at: ${video.current.getCurrentTime()}`);
    const newFrame = captureVideoFrame(
      video.current.getInternalPlayer(),
      "jpeg"
    );
    setCapturedFrames([...capturedFrames, newFrame]);
    setCurrentFrameTime(null);
  };

  const finishUpload = (bucketName, path) => {
    const newValue = {
      storage_type: "s3",
      bucket_name: bucketName,
      path: path,
    };

    setValues({ ...values, [block.screen_capture.id]: newValue });
    setUploadCompletionStatus("completed");
    setIsFinished(true);
  };

  //Helper function to call the track-upload api
  async function createUploadRecord(file) {
    const { data } = await axios.post(
      `${config.CONTRIBUTOR_API_ENDPOINT}/api/v3/media_upload/`,
      {
        file_name: file.name,
        file_size: file.size,
        content_type: file.type,
        cid,
        srid,
      },
      { headers: { "Content-Type": "application/json" } }
    );
    setMediaUploadId(data.media_upload_id);
    return data.media_upload_id;
  }

  async function updateUploadStatus(mediaUploadId, status) {
    return axios.patch(
      `${config.CONTRIBUTOR_API_ENDPOINT}/api/v3/media_upload/${mediaUploadId}/`,
      { upload_status: status },
      { headers: { "Content-Type": "application/json" } }
    );
  }

  const runValidationWithCapturedFrames = async (frames) => {
    console.log("Uploading frames for validation...");
    setStatusMessage(getString("validation_1_validating", lang));

    const formDataForValidation = new FormData();
    formDataForValidation.append("platform", platform);
    formDataForValidation.append("screen_capture_id", block.screen_capture.id);

    // add each extracted frame to the form
    for (let i = 0; i < frames.length; i++) {
      let frame = frames[i];
      formDataForValidation.append(
        `frame-${i}`,
        frame.blob,
        `frame-${i}.${frame.format}`
      );
    }

    let url = `${baseJobURL}/validate-screen-capture`;

    let video_file_media_upload_id;

    try {
      video_file_media_upload_id = await createUploadRecord(videoFile);
    } catch (err) {
      console.error("failed to create upload record");
    }
    axios
      .post(url, formDataForValidation)
      .then((response) => {
        if (response.data.validated) {
          setStatusMessage(getString("validation_2_preparing", lang));
          console.log("Validated");

          if (useMultipartUpload) {
            console.log("Using multipart upload");
            console.log("Getting an upload path");
            let url = `${baseJobURL}/generate-upload-path`;
            axios
              .post(url, { filename: videoFile.name, cid: cid, srid: srid })
              .then((response) => {
                setStatusMessage(getString("validation_3_uploading", lang));
                const path = response.data.path;
                console.log(`Upload path: ${path}`);

                // whacking this value into a local variable otherwise the uploader complains that it's empty
                const file = videoFile;

                evaporateUploader.then((evaporate) => {
                  evaporate
                    .add({
                      file,
                      name: path,
                      progress: (progressValue) => {
                        setUploadProgressPercentage(progressValue * 100);
                      },
                      complete: (_xhr, awsKey) => {
                        console.log(`File upload complete: ${awsKey}`);
                      },
                    })
                    .then(
                      (awsObjectKey) => {
                        return updateUploadStatus(
                          video_file_media_upload_id,
                          "uploaded"
                        )
                          .catch((err) => {
                            console.error("Failed to mark uploaded:", err);
                          })
                          .then(() => {
                            setStatusMessage(
                              getString("validation_4_finished", lang)
                            );
                            console.log(
                              `Finished multipart upload: ${awsObjectKey}`
                            );

                            setShowSpinner(false);
                            finishUpload(config.USER_UPLOAD_S3_BUCKET, path);
                          });
                      },
                      (reason) => {
                        return updateUploadStatus(
                          video_file_media_upload_id,
                          "failed"
                        )
                          .catch((err) => {
                            console.error("Failed to mark failed:", err);
                          })
                          .then(() => {
                            setStatusMessage(
                              getString("validation_5_error_uploading", lang)
                            );
                            console.log(`Error in multipart upload: ${reason}`);
                            setShowSpinner(false);
                            setUploadCompletionStatus("terminated");
                            setIsFinished(true);
                          });
                      }
                    );
                });
              })
              .catch((error) => {
                setStatusMessage(
                  getString("validation_6_error_preparing", lang)
                );
                console.log("Error getting upload path");
                setShowSpinner(false);
                setUploadCompletionStatus("terminated");
                setIsFinished(true);
              });
          } else {
            console.log("Using simple upload");
            const formDataForUploadURL = new FormData();
            formDataForUploadURL.append("filename", videoFile.name);
            formDataForUploadURL.append("content_type", videoFile.type);
            formDataForUploadURL.append("cid", cid);
            formDataForUploadURL.append("srid", srid);

            let url = `${baseJobURL}/generate-presigned-upload-url`;
            axios
              .post(url, formDataForUploadURL)
              .then(function (response) {
                setStatusMessage(getString("validation_3_uploading", lang));
                console.log(`Uploading: ${videoFile.name}`);

                const url = response.data.url;
                const contentType = response.data.content_type;
                const bucketName = response.data.bucket_name;
                const path = response.data.path;

                axios
                  .put(url, videoFile, {
                    headers: {
                      "Content-Type": contentType,
                    },
                  })
                  .then((response) => {
                    setStatusMessage(getString("validation_4_finished", lang));
                    console.log("Finished upload");
                    setShowSpinner(false);
                    finishUpload(bucketName, path);
                  })
                  .catch((error) => {
                    setStatusMessage(
                      getString("validation_5_error_uploading", lang)
                    );
                    console.log("Error uploading");
                    setShowSpinner(false);
                    setUploadCompletionStatus("terminated");
                    setIsFinished(true);
                  });
              })
              .catch((error) => {
                setStatusMessage(
                  getString("validation_6_error_preparing", lang)
                );
                console.log("Error getting upload URL");
                setShowSpinner(false);
                setUploadCompletionStatus("terminated");
                setIsFinished(true);
              });
          }
        } else {
          console.log("Validation failed");

          // if we're under the max attempts, then go around again
          const nextValidationAttempt = validationAttempts + 1;
          if (
            captureType === "video" &&
            nextValidationAttempt < maxValidationAttempts
          ) {
            setValidationAttempts(nextValidationAttempt);
            setStatusMessage(getString("validation_7_trying_again", lang));
            console.log("Trying again...");
            extractFramesForValidation(nextValidationAttempt);
          } else {
            setShowSpinner(false);
            setHasUploadError(true);

            const c = uploadAttemptConfig[uploadAttempts];
            if (c.autoAdvanceWithTerminate) {
              handleTerminate();
              return;
            }

            setStatusMessage(c.statusTextWithError);
            if (c.allowUpload) {
              resetVideo();
            }
            setShowInstructionsButton(c.showInstructionsLink);
            setShowTerminateButton(c.showTerminateLink);
          }
        }
      })
      .catch(function (error) {
        console.log("Error from server during validation");
        setStatusMessage(
          getString("validation_8_error_during_validation", lang)
        );
        setShowSpinner(false);
        setUploadCompletionStatus("terminated");
        setIsFinished(true);
        handleTerminate();
      });
  };

  const FILTERED_EXTENSIONS = [
    // Images
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".webp",
    ".tiff",
    ".heic",
    ".svg",
    // Videos
    ".mp4",
    ".mov",
    ".avi",
    ".wmv",
    ".flv",
    ".mkv",
    ".webm",
    ".m4v",
    // Audio
    ".mp3",
    ".wav",
    ".aac",
    ".ogg",
    ".flac",
    ".m4a",
    ".wma",
  ];

  // Upload management function that starts when user explicitly clicks upload
  const handleConfirmUpload = async () => {
    if (!selectedMultiFiles) return;

    const failedFiles = [];

    for (const file of selectedMultiFiles) {
      if (
        block.screen_capture.validation &&
        block.screen_capture.validation.length > 0
      ) {
        const failedTests = validateFileUpload(file);
        if (failedTests.length > 0) {
          let fileErrorMessage = ``;
          failedTests.forEach((test, index) => {
            fileErrorMessage += getFileValidationErrorMessage(test);
            if (index + 1 < failedTests.length) {
              fileErrorMessage += `\n`;
            }
          });
          failedFiles.push({
            fileName: file.name,
            errors: fileErrorMessage,
          });
        }
      }
    }

    if (failedFiles.length > 0) {
      const combinedErrorMessage = (
        <div>
          <span className="error">
            Validation failed for {failedFiles.length} file(s):
          </span>
          {failedFiles.map((failedFile, index) => (
            <div key={index}>
              <strong>{failedFile.fileName}:</strong> {failedFile.errors}
            </div>
          ))}
        </div>
      );

      setHasUploadError(true);
      setStatusMessage(combinedErrorMessage);
      return;
    }

    setShowUploadConfirmation(false);
    setIsProcessingFiles(true);
    setStatusMessage(getString("validation_2b_processing_files", lang));

    try {
      // const mergedBlob = await mergeZips(selectedMultiFiles);
      const mergedBlob = await mergeZips(
        selectedMultiFiles,
        { arrangement: "byName" },
        (progress) => {
          setZipProgress((progress * 100).toFixed(1));
        }
      );
      const mergedFile = new File([mergedBlob], "takeout.zip", {
        type: mergedBlob.type,
        lastModified: Date.now(),
      });

      setVideoFile(mergedFile);
      setUploadingWithoutValidation(true);
      setVideoFilePath(URL.createObjectURL(mergedFile));
    } catch (error) {
      console.error("Error Processing Files", error);
      setHasUploadError(true);
      setStatusMessage("Error processing selected files. Please try again.");
    } finally {
      setIsProcessingFiles(false);
    }
  };

  const handleCancelUpload = () => {
    setSelectedMultiFiles(null);
    setShowUploadConfirmation(false);
    setFileInputValue("");
  };

  const validateZipFile = async (file) => {
    const zipReader = new ZipReader(new BlobReader(file));
    const entries = await zipReader.getEntries();
    const filenames = entries.map((entry) => entry.filename);

    return { entries, filenames };
  };

  const isFilteredFile = (filename) => {
    const lowerFilename = filename.toLowerCase();
    return FILTERED_EXTENSIONS.some((ext) => lowerFilename.endsWith(ext));
  };

  const processZipFile = async (originalEntries) => {
    const zipWriter = new ZipWriter(new BlobWriter());

    const filesToProcess = originalEntries.filter(
      (entry) => !isFilteredFile(entry.filename)
    );

    for (let i = 0; i < filesToProcess.length; i++) {
      const entry = filesToProcess[i];

      if (entry.directory) {
        continue; // skip folders
      }

      // Copy the entry's compressed bytes as-is instead of inflating and
      // re-deflating them. Large exports (e.g. a 1 GB conversations.json)
      // otherwise take minutes and several GB of memory in the browser.
      const rawBlob = await entry.getData(new BlobWriter(), {
        passThrough: true,
      });
      await zipWriter.add(entry.filename, new BlobReader(rawBlob), {
        passThrough: true,
        compressionMethod: entry.compressionMethod,
        uncompressedSize: entry.uncompressedSize,
        signature: entry.signature,
        lastModDate: entry.lastModDate,
      });
    }

    return await zipWriter.close();
  };

  const uploadFile = async () => {
    console.log("Uploading file...");
    if (
      block.screen_capture.validation &&
      block.screen_capture.validation.length > 0
    ) {
      const failedTests = validateFileUpload(videoFile);
      if (failedTests.length > 0) {
        let errorMessage = ``;
        failedTests.forEach((test, index) => {
          errorMessage += getFileValidationErrorMessage(test);
          if (index + 1 < failedTests.length) {
            errorMessage += `\n`;
          }
        });
        errorMessage = (
          <p>
            <span className="error">
              Validation Error{failedTests.length > 1 ? "s" : ""}: {"\n"}
            </span>
            {errorMessage}
          </p>
        );
        setHasUploadError(true);
        setStatusMessage(errorMessage);
        setShowInstructionsButton(true);
        setShowTerminateButton(false);
        resetVideo();
        return;
      }
    }

    let processedFile = null;

    if (block.screen_capture.use_media_file_thinning) {
      console.log("Removing media from zip archive");
      setStatusMessage(getString("validation_2b_removing_media", lang));
      console.log("video file", videoFile);

      // filter the zip file for any media files
      const { entries } = await validateZipFile(videoFile);
      // Preserve the rejection previously raised when extracting encrypted data.
      if (
        entries.some(
          (entry) =>
            !entry.directory &&
            !isFilteredFile(entry.filename) &&
            entry.encrypted
        )
      ) {
        throw new Error("File contains encrypted entry");
      }
      const hasMedia = entries.some(
        (entry) => !entry.directory && isFilteredFile(entry.filename)
      );

      if (hasMedia) {
        const processedBlob = await processZipFile(entries);

        // recreate the zip without filtered files
        processedFile = new File(
          [processedBlob],
          videoFile.name.replace(".zip", "-filtered.zip"),
          { type: "application/zip" }
        );
      } else {
        // nothing to strip, so upload the original archive untouched
        console.log("No media files in zip archive, skipping rewrite");
        processedFile = videoFile;
      }
    } else {
      processedFile = videoFile;
    }

    if (useMultipartUpload) {
      console.log("Using multipart upload for files");
      console.log("Getting an upload path");
      let url = `${baseJobURL}/generate-upload-path`;

      //Media-Upload API
      let fileMediaUploadId
      try {
        fileMediaUploadId = await createUploadRecord(processedFile);
      } catch (error) {
        console.error("failed to create upload record");
      }
      axios
        .post(url, { filename: processedFile.name, cid: cid, srid: srid })
        .then((response) => {
          setStatusMessage(getString("validation_3_uploading", lang));
          const path = response.data.path;
          console.log(`Upload path: ${path}`);

          // whacking this value into a local variable otherwise the uploader complains that it's empty
          const file = processedFile;

          evaporateUploader.then((evaporate) => {
            evaporate
              .add({
                file,
                name: path,
                progress: (progressValue) => {
                  setUploadProgressPercentage(progressValue * 100);
                },
                complete: (_xhr, awsKey) => {
                  console.log(`File upload complete: ${awsKey}`);
                },
              })
              .then(
                (awsObjectKey) => {
                  // return the promise so the chain waits for it
                  return updateUploadStatus(fileMediaUploadId, "uploaded")
                    .catch((err) => {
                      console.error("Failed to mark uploaded:", err);
                    })
                    .then(() => {
                      setStatusMessage(
                        getString("validation_4_finished", lang)
                      );
                      console.log(`Finished multipart upload: ${awsObjectKey}`);
                      setShowSpinner(false);
                      finishUpload(config.USER_UPLOAD_S3_BUCKET, path);
                    });
                },
                (reason) => {
                  return updateUploadStatus(fileMediaUploadId, "failed")
                    .catch((err) => {
                      console.error("Failed to mark failed:", err);
                    })
                    .then(() => {
                      setStatusMessage(
                        getString("validation_5_error_uploading", lang)
                      );
                      console.log(`Error in multipart upload: ${reason}`);
                      setShowSpinner(false);
                      setUploadCompletionStatus("terminated");
                      setIsFinished(true);
                    });
                }
              );
          });
        })
        .catch((error) => {
          setStatusMessage(getString("validation_6_error_preparing", lang));
          console.log("Error getting upload path");
          setShowSpinner(false);
          setUploadCompletionStatus("terminated");
          setIsFinished(true);
        });
    } else {
      console.log("Using simple upload");
      const formDataForUploadURL = new FormData();
      formDataForUploadURL.append("filename", videoFile.name);
      formDataForUploadURL.append("content_type", videoFile.type);
      formDataForUploadURL.append("cid", cid);
      formDataForUploadURL.append("srid", srid);

      let url = `${baseJobURL}/generate-presigned-upload-url`;
      axios
        .post(url, formDataForUploadURL)
        .then(function (response) {
          setStatusMessage(getString("validation_3_uploading", lang));
          console.log(`Uploading: ${videoFile.name}`);

          const url = response.data.url;
          const contentType = response.data.content_type;
          const bucketName = response.data.bucket_name;
          const path = response.data.path;

          axios
            .put(url, videoFile, {
              headers: {
                "Content-Type": contentType,
              },
            })
            .then((response) => {
              setStatusMessage(getString("validation_4_finished", lang));
              setShowSpinner(false);
              finishUpload(bucketName, path);
            })
            .catch((error) => {
              setStatusMessage(getString("validation_5_error_uploading", lang));
              console.log("Error uploading");
              setShowSpinner(false);
              setUploadCompletionStatus("terminated");
              setIsFinished(true);
            });
        })
        .catch((error) => {
          setStatusMessage(getString("validation_6_error_preparing", lang));
          console.log("Error getting upload URL");
          setShowSpinner(false);
          setUploadCompletionStatus("terminated");
          setIsFinished(true);
        });
    }
  };

  const extractFramesForValidation = (attempt) => {
    console.log("Extracting sample frames for attempt " + attempt);

    //  simple scheme to grab 5 equally-spaced frames (to be improved)
    const duration = video.current.getDuration();

    // once these values are set, the extraction hook will notice and kick off extraction
    let frames = [];
    if (attempt === 0) {
      frames = [1, 2, 3, 4].map((n) => {
        return ((duration / 4) * n).toFixed(2);
      });
    } else if (attempt === 1) {
      frames = [1, 2, 3, 4, 5].map((n) => {
        return ((duration / 5) * n).toFixed(2);
      });
    } else {
      frames = [1, 2, 3, 4, 5, 6, 7].map((n) => {
        return ((duration / 7) * n).toFixed(2);
      });
    }

    console.log(`Extracting frames: ${frames}`);
    setFrameTimesToCapture(frames);
    setCapturedFrames([]);
    setExtractingFrames(true);
  };

  const validateAndUpload = () => {
    setShowSpinner(true);
    setShowInstructionsButton(false);
    setShowTerminateButton(false);

    if (captureType === "video") {
      setStatusMessage(getString("sampling_content", lang));
      extractFramesForValidation(1);
    } else if (captureType === "image") {
      setStatusMessage(getString("sampling_content", lang));
      setCapturedFrames([]);
      setExtractingFrames(true);
    }
  };

  const validateFileUpload = (file) => {
    let testResults = [];
    for (const test of block.screen_capture.validation) {
      let result = { result: true };
      if (test.type === FILE_VALIDATION_TYPES.name) {
        result = validateFileName(file.name, test.params);
      } else if (test.type === FILE_VALIDATION_TYPES.recency) {
        // Disabled for now
        // result = validateFileRecency(file.lastModifiedDate, test.params)
      } else if (test.type === FILE_VALIDATION_TYPES.size) {
        // Disabled for now
        // result = validateFileSize(file.size, test.params)
      }
      testResults.push(result);
    }
    return testResults.filter((test) => !test.result);
  };

  const validateFileName = (name, params) => {
    const result = name.match(new RegExp(params.regex)) ? true : false;
    console.log("name validation: ", result);
    return {
      type: FILE_VALIDATION_TYPES.name,
      result,
      error_msg: params.error_msg,
    };
  };

  const validateFileRecency = (updatedDate, params) => {
    if (!updatedDate || new Date(updatedDate).getFullYear() <= 1970) {
      console.log("recency validation: true (epoch)");
      return {
        type: FILE_VALIDATION_TYPES.recency,
        result: true,
      };
    }
    const result =
      Date.now() - updatedDate <= params.max_age_in_minutes * 60 * 1000;
    console.log("recency validation: ", result);
    return {
      type: FILE_VALIDATION_TYPES.recency,
      result,
      error_msg: params.error_msg,
    };
  };

  const validateFileSize = (size, params) => {
    const result = size >= params.lower && size <= params.upper;
    console.log("size validation: ", result);
    return {
      type: FILE_VALIDATION_TYPES.size,
      result,
      error_msg: params.error_msg,
    };
  };

  const getFileValidationErrorMessage = (test) => {
    if (test.error_msg && test.error_msg[lang]) {
      return test.error_msg[lang];
    }
    return FILE_VALIDATION_ERROR_MESSAGES[test.type][lang];
  };

  const resetVideo = () => {
    setFileInputValue("");
    setVideoFile(null);
    setVideoFilePath(null);
    setValidationAttempts(1);
  };

  //Merge Zip Function - Stream API
  async function mergeZips(files, options = {}, onProgress = () => {}) {
    const { arrangement = "byName" } = options;
    const zipFiles = Array.from(files).filter((f) =>
      f.name.toLowerCase().endsWith(".zip")
    );
    if (!zipFiles.length) {
      throw new Error("No zip files provided");
    }

    // Set up output stream + ZIP writer (no compression for max speed)
    const { readable, writable } = new TransformStream();
    const writer = new ZipWriter(writable, { level: 0 });
    const resultPromise = new Response(readable).blob();

    // Gather all entries & count them
    let totalFiles = 0;
    let processedFiles = 0;
    const allEntries = [];

    for (const file of zipFiles) {
      const reader = new ZipReader(new BlobReader(file));
      const entries = (await reader.getEntries()).filter((e) => !e.directory);

      totalFiles += entries.length;
      for (const entry of entries) {
        const folder = file.name.replace(/\.zip$/i, "");
        const outputPath =
          arrangement === "byName"
            ? `${folder}/${entry.filename}`
            : entry.filename;

        allEntries.push({ entry, outputPath, reader });
      }
    }

    // Process one entry at a time via streaming
    for (const { entry, outputPath } of allEntries) {
      const inStream = new ReadableStream({
        start(ctrl) {
          entry.getData(
            new WritableStream({
              write(chunk) {
                ctrl.enqueue(chunk);
              },
              close() {
                ctrl.close();
              },
              abort(err) {
                ctrl.error(err);
              },
            }),
            { transferStreams: true }
          );
        },
      });

      await writer.add(outputPath, { readable: inStream }, { level: 0 });
      processedFiles++;
      onProgress(processedFiles / totalFiles);
    }

    // Clean up readers, close writer, and return blob
    const uniqueReaders = [...new Set(allEntries.map((e) => e.reader))];
    await Promise.all(uniqueReaders.map((r) => r.close()));
    await writer.close();

    onProgress(1);
    return resultPromise;
  }

  // Simple file size format function for selected file block
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";

    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const handleFileSelection = async (event) => {
    setHasUploadError(false);
    const files = event.target.files;

    // Added conditional logic to check for multiple file upload flag
    if (block.screen_capture.allow_multiple_file_upload) {
      const newFiles = Array.from(files);

      //Add file limit and enforce this on select
      const maxFiles = block.screen_capture.max_file_count || 3; // Default to 3
      if (newFiles.length > maxFiles) {
        setHasUploadError(true);
        setStatusMessage(
          <p>
            <span className="error">Too many files selected</span> — Please
            select no more than {maxFiles} files at once.
          </p>
        );
        return;
      }

      // Check if we already have selected files
      if (selectedMultiFiles && selectedMultiFiles.length > 0) {
        // Create a Set to track existing file identifiers to avoid duplicates
        const existingFileIds = new Set(
          selectedMultiFiles.map(
            (file) => `${file.name}-${file.size}-${file.lastModified}`
          )
        );
        // Filter out duplicates
        const uniqueNewFiles = newFiles.filter((file) => {
          const fileId = `${file.name}-${file.size}-${file.lastModified}`;
          return !existingFileIds.has(fileId);
        });

        // Append new unique files to existing selection
        setSelectedMultiFiles([...selectedMultiFiles, ...uniqueNewFiles]);
      } else {
        // First selection - just set the files
        setSelectedMultiFiles(newFiles);
      }
      setShowUploadConfirmation(true);
    } else if (files.length > 0) {
      const file = event.target.files[0];

      if (captureType === "video") {
        if (file.type.toLowerCase().startsWith("video")) {
          setNewVideoToValidate(true);
          setFileInputValue(event.target.value);
          setVideoFile(file);
          setVideoFilePath(URL.createObjectURL(file));
        } else {
          setStatusMessage(getString("wrong_type_video", lang));
          resetVideo();
          setShowSpinner(false);

          const c = uploadAttemptConfig[uploadAttempts];
          setShowInstructionsButton(c.showInstructionsLink);
          setShowTerminateButton(c.showTerminateLink);
        }
      } else if (captureType === "image") {
        if (file.type.toLowerCase().startsWith("image")) {
          setNewVideoToValidate(true);
          setFileInputValue(event.target.value);
          setVideoFile(file);
          setVideoFilePath(URL.createObjectURL(file));
        } else {
          setStatusMessage(getString("wrong_type_image", lang));
          resetVideo();
          setShowSpinner(false);

          const c = uploadAttemptConfig[uploadAttempts];
          setShowInstructionsButton(c.showInstructionsLink);
          setShowTerminateButton(c.showTerminateLink);
        }
      } else if (captureType === "file") {
        setUploadingWithoutValidation(true);
        setFileInputValue(event.target.value);
        setVideoFile(file);
        setVideoFilePath(URL.createObjectURL(file));
      }
    } else {
      resetVideo();
    }
  };

  const handleOnReady = (event) => {
    console.log("File loaded (handleOnReady)");
    if (newVideoToValidate) {
      setNewVideoToValidate(false);
      setUploadAttempts(uploadAttempts + 1);
      validateAndUpload();
    }
  };

  const handleShowInstructions = (event) => {
    setHasUploadError(false);
    showInstructions();
  };

  if (isFinished) {
    if (uploadCompletionStatus === "completed") {
      setTimeout(goNext, 300);
      return (
        <p className="full-page-message">
          {getString("upload_successful", lang)}
        </p>
      );
    } else {
      return (
        <div className="full-page-message">
          <Spinner className="full-page-spinner" type="grow" />
        </div>
      );
    }
  } else {
    const showVideoError =
      !uploadAttemptConfig[uploadAttempts].allowUpload && hasUploadError;
    return (
      <React.Fragment>
        <Input
          type="file"
          className="hidden-file-input"
          id="screenCaptureFile"
          value={fileInputValue}
          onChange={handleFileSelection}
          multiple={block.screen_capture.allow_multiple_file_upload}
        />

        <div
          className={
            "uploaded-video-container " + (showVideoError ? "video-error" : "")
          }
        >
          {!videoFilePath && effectiveFileSource !== "localStorage" && (
            <label htmlFor="screenCaptureFile" className="link-like-text">
              {uploadAttemptConfig[uploadAttempts].uploadLinkText}
            </label>
          )}

          {effectiveFileSource === "localStorage" && <p>Fetching Orders</p>}

          {videoFilePath && captureType === "video" && (
            <div className="upload-player-wrapper">
              <ReactPlayer
                url={videoFilePath}
                ref={video}
                onSeek={handleSeekFinished}
                onReady={handleOnReady}
                width="100%"
                height="100%"
              />
            </div>
          )}
          {videoFilePath && captureType === "image" && (
            <div className="upload-player-wrapper">
              <img
                src={videoFilePath}
                alt="Selected image"
                onLoad={handleOnReady}
              />
            </div>
          )}
        </div>

        {/* Added simple component to show selected files with Upload and Clear Buttons */}
        {showUploadConfirmation && selectedMultiFiles && !isProcessingFiles && (
          <div className="upload-confirmation-below">
            <div className="selected-files-info">
              <div className="file-count">
                {selectedMultiFiles.length} files selected
              </div>
              <div className="file-list">
                {Array.from(selectedMultiFiles).map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name} ({formatFileSize(file.size)})
                  </div>
                ))}
              </div>
            </div>
            <div className="upload-actions">
              <button
                onClick={handleConfirmUpload}
                disabled={isProcessingFiles}
                className="confirm-upload-btn"
              >
                {isProcessingFiles ? "Processing..." : "Upload"}
              </button>
              <button
                onClick={handleCancelUpload}
                disabled={isProcessingFiles}
                className="cancel-upload-btn"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {statusMessage && (
          <div className="uploader-status-message">{statusMessage}</div>
        )}

        {uploadProgressPercentage > 0 && (
          <Progress
            animated
            className="upload-progress-bar"
            color="primary"
            value={uploadProgressPercentage}
            max={100}
          />
        )}

        {isProcessingFiles && zipProgress > 0 && (
          <Progress
            animated
            value={zipProgress}
            max={100}
            color="info"
            className="upload-progress-bar"
          />
        )}

        {uploadProgressPercentage <= 0 && showSpinner && (
          <div className="uploader-spinner-wrapper">
            <Spinner className="uploader-spinner" type="grow" />
          </div>
        )}

        {showInstructionsButton && showInstructions && (
          <p
            onClick={handleShowInstructions}
            className="instructions-button-container link-like-text"
          >
            {getString("back_to_instructions", lang)}
          </p>
        )}

        {showTerminateButton && (
          <p
            className="terminate-button-container link-like-text warning"
            onClick={handleUserTerminate}
          >
            {getString("skip_this", lang)}
          </p>
        )}
      </React.Fragment>
    );
  }
}

export default Uploader;
